import { applyCustomTheme, applyDieterColors, makeTheme } from '@app/app-provider';
import { Logo } from '@components/block/Header/Logo';
import { Alert, Button, CircularProgress, TextField, ThemeProvider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useEffect, useMemo, useState } from 'react';
import { useAsyncCallback } from 'react-async-hook';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

interface Branding {
  principalName: string;
  logoB64: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  successColor: string | null;
  warningColor: string | null;
  dangerColor: string | null;
  emailFooter: string | null;
}

interface FormState {
  companyName: string;
  street: string;
  zip: string;
  city: string;
  website: string;
  phone: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
}

const emptyForm: FormState = {
  companyName: '',
  street: '',
  zip: '',
  city: '',
  website: '',
  phone: '',
  contactFirstName: '',
  contactLastName: '',
  contactEmail: '',
};

export function RegisterTenant() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const [brandingLoading, setBrandingLoading] = useState(true);
  const [branding, setBranding] = useState<Branding | null>(null);
  const [invalidLink, setInvalidLink] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBrandingLoading(true);
    fetch(`/api/tenant-registrations/${encodeURIComponent(slug)}`)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          setInvalidLink(true);
          return;
        }
        const json = await res.json();
        if (json?.ok && json.data) {
          setBranding(json.data as Branding);
        } else {
          setInvalidLink(true);
        }
      })
      .catch(() => {
        if (!cancelled) setInvalidLink(true);
      })
      .finally(() => {
        if (!cancelled) setBrandingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Apply the principal's whitelabel branding the same way the app does globally:
  // set the CSS variables (used by the Tailwind color scale) from the resolved
  // colors, falling back to the default Dieter palette.
  useEffect(() => {
    if (!branding) return;
    if (branding.primaryColor && branding.secondaryColor) {
      applyCustomTheme(
        branding.primaryColor,
        branding.secondaryColor,
        branding.successColor ?? undefined,
        branding.warningColor ?? undefined,
        branding.dangerColor ?? undefined
      );
    } else {
      applyDieterColors();
    }
  }, [branding]);

  const brandTheme = useMemo(
    () =>
      branding?.primaryColor && branding?.secondaryColor
        ? makeTheme(
            branding.primaryColor,
            branding.secondaryColor,
            branding.successColor ?? undefined,
            branding.warningColor ?? undefined,
            branding.dangerColor ?? undefined
          )
        : makeTheme('#ab5fce', '#fdd213'),
    [branding]
  );

  const handleChange =
    (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const submit = useAsyncCallback(async () => {
    setError(null);
    const res = await fetch(`/api/tenant-registrations/${encodeURIComponent(slug)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) {
      setError(
        json?.error ||
          t('route.register-tenant.error', 'Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.')
      );
      return;
    }
    setSubmitted(true);
  });

  if (brandingLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <CircularProgress />
      </div>
    );
  }

  if (invalidLink) {
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <Alert severity="error" className="max-w-md">
          {t('route.register-tenant.invalid-link', 'Dieser Registrierungslink ist ungültig oder nicht mehr aktiv.')}
        </Alert>
      </div>
    );
  }

  return (
    <ThemeProvider theme={brandTheme}>
      <div className="min-h-screen bg-gray-50 py-10 px-4">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-8">
          <div className="flex flex-col items-center mb-6 text-center">
            <div className="mb-4">
              <Logo clickable={false} src={branding?.logoB64 ?? null} alt={branding?.principalName} maxWidth="220px" />
            </div>
            <h1 className="text-2xl font-semibold" style={{ color: 'var(--primary-root)' }}>
              {t('route.register-tenant.header', 'Registrierung')}
            </h1>
          </div>

          {submitted ? (
            <div className="flex flex-col items-center text-center gap-3 py-8">
              <CheckCircleIcon color="success" sx={{ fontSize: 56 }} />
              <h2 className="text-xl font-medium">{t('route.register-tenant.success-title', 'Vielen Dank!')}</h2>
              <p className="text-gray-600 max-w-md">
                {t(
                  'route.register-tenant.success-body',
                  'Ihre Registrierungsanfrage wurde übermittelt. Sie werden per E-Mail benachrichtigt, sobald Ihr Zugang freigeschaltet wurde.'
                )}
              </p>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit.execute();
              }}
              className="flex flex-col gap-4"
            >
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                {t('route.register-tenant.section.company', 'Unternehmen')}
              </h3>
              <TextField
                required
                label={t('route.register-tenant.field.company-name', 'Firmenname')}
                value={form.companyName}
                onChange={handleChange('companyName')}
                fullWidth
              />
              <TextField
                required
                label={t('route.register-tenant.field.street', 'Straße und Hausnummer')}
                value={form.street}
                onChange={handleChange('street')}
                fullWidth
              />
              <div className="flex gap-4">
                <TextField
                  required
                  label={t('route.register-tenant.field.zip', 'PLZ')}
                  value={form.zip}
                  onChange={handleChange('zip')}
                  className="w-1/3"
                />
                <TextField
                  required
                  label={t('route.register-tenant.field.city', 'Ort')}
                  value={form.city}
                  onChange={handleChange('city')}
                  className="w-2/3"
                />
              </div>
              <TextField
                label={t('route.register-tenant.field.website', 'Website (optional)')}
                value={form.website}
                onChange={handleChange('website')}
                fullWidth
              />
              <TextField
                required
                label={t('route.register-tenant.field.phone', 'Telefon')}
                value={form.phone}
                onChange={handleChange('phone')}
                fullWidth
              />

              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mt-2">
                {t('route.register-tenant.section.contact', 'Ansprechpartner')}
              </h3>
              <div className="flex gap-4">
                <TextField
                  required
                  label={t('route.register-tenant.field.first-name', 'Vorname')}
                  value={form.contactFirstName}
                  onChange={handleChange('contactFirstName')}
                  className="w-1/2"
                />
                <TextField
                  required
                  label={t('route.register-tenant.field.last-name', 'Nachname')}
                  value={form.contactLastName}
                  onChange={handleChange('contactLastName')}
                  className="w-1/2"
                />
              </div>
              <TextField
                required
                type="email"
                label={t('route.register-tenant.field.email', 'E-Mail')}
                value={form.contactEmail}
                onChange={handleChange('contactEmail')}
                fullWidth
              />

              {error && <Alert severity="error">{error}</Alert>}

              <Button type="submit" variant="contained" color="primary" size="large" disabled={submit.loading}>
                {submit.loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('route.register-tenant.submit', 'Registrierung absenden')
                )}
              </Button>
              {branding.emailFooter && (
                <div
                  className="text-center text-xs text-gray-500 mt-4"
                  dangerouslySetInnerHTML={{ __html: branding.emailFooter }}
                />
              )}
            </form>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
}
